import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ExpandableSection from "@components/animations/ExpandableSection";
import Button from "@components/buttons/Button";
import styles from "@components/schedule/ScheduleSession.module.css";
import Icon from "@components/text/Icon";
import IconBase from "@components/text/IconBase";
import Heading1 from "@components/typography/Heading1";
import Heading2 from "@components/typography/Heading2";
import { ContactMethodEnum } from "@typing/Generated";
import { imageUrl } from "@utils/imageUtils";

type Props = {
  contactMethod: ContactMethodEnum | undefined;
  onContactMethodSelected: (contactMethod: ContactMethodEnum) => void;
};

const ContactMethod = ({ contactMethod, onContactMethodSelected }: Props) => {
  const intl = useIntl();
  const [isHovered, setHovered] = useState<ContactMethodEnum | null>(null);
  const [editContactMethod, setEditContactMethod] = useState<ContactMethodEnum | undefined>(contactMethod);
  const buttonsRef = useRef<HTMLDivElement>(null);

  return (
    <div className={styles.centerContent}>
      <Heading1>
        <FormattedMessage id="pages.consults.contactMethod.title" />
      </Heading1>
      <div className={styles.contactMethods}>
        <motion.div
          animate={editContactMethod === ContactMethodEnum.PHONE ? "blurred" : "show"}
          className={styles.contactMethod}
          data-test="contact-method-zoom"
          onClick={() => setEditContactMethod(ContactMethodEnum.ZOOM)}
          onMouseEnter={() => setHovered(ContactMethodEnum.ZOOM)}
          onMouseLeave={() => setHovered(null)}
          variants={{ blurred: { opacity: 0.3, scale: 1 }, show: { scale: 1 } }}
        >
          <motion.img
            animate={
              editContactMethod === ContactMethodEnum.ZOOM
                ? "show"
                : isHovered === ContactMethodEnum.ZOOM
                  ? "show"
                  : "hidden"
            }
            aria-label={intl.formatMessage({ id: "accessibility.ariaLabels.zoom" })}
            className={`${styles.zoomImg} ${
              editContactMethod === ContactMethodEnum.ZOOM ? styles.contactMethodActive : ""
            }`}
            src={imageUrl("zoom")}
            transition={{ stiffness: 100, type: "spring" }}
            variants={{ hidden: { scale: 1 }, show: { scale: 1.1 } }}
          />
          <div className={styles.contactMethodTitle}>
            <Heading2>
              <FormattedMessage id="pages.consults.contactMethod.zoom" />
            </Heading2>
          </div>
        </motion.div>
        <motion.div
          animate={editContactMethod === ContactMethodEnum.ZOOM ? "blurred" : "show"}
          className={styles.contactMethod}
          onClick={() => setEditContactMethod(ContactMethodEnum.PHONE)}
          onMouseEnter={() => setHovered(ContactMethodEnum.PHONE)}
          onMouseLeave={() => setHovered(null)}
          variants={{ blurred: { opacity: 0.3, scale: 1 }, show: { scale: 1 } }}
        >
          <motion.div
            animate={
              editContactMethod === ContactMethodEnum.PHONE
                ? "show"
                : isHovered === ContactMethodEnum.PHONE
                  ? "show"
                  : "hidden"
            }
            className={`${styles.iconContainer} ${
              editContactMethod === ContactMethodEnum.PHONE ? styles.contactMethodActive : ""
            }`}
            transition={{ stiffness: 100, type: "spring" }}
            variants={{ hidden: { scale: 1 }, show: { scale: 1.1 } }}
          >
            <Icon color="white" icon="phone" size="xlarge" />
          </motion.div>
          <div className={styles.contactMethodTitle}>
            <Heading2>
              <FormattedMessage id="pages.consults.contactMethod.phone" />
            </Heading2>
          </div>
        </motion.div>
      </div>
      <div className={styles.buttonContainer} ref={buttonsRef}>
        <ExpandableSection isOpen={!!editContactMethod} key="contact-method" transition={{ duration: 0.6 }}>
          <div>
            {editContactMethod && (
              <div className={styles.textExplanation}>
                <Heading2 color="gray-500">
                  <FormattedMessage id={`pages.consults.contactMethod.description.${editContactMethod}`} />
                </Heading2>
              </div>
            )}

            <Button
              disabled={!editContactMethod}
              onClick={() => {
                if (editContactMethod) {
                  onContactMethodSelected(editContactMethod);
                }
              }}
              size="large"
            >
              <FormattedMessage id="pages.consults.contactMethod.review" />
              <IconBase icon="checkmark" slot="end" />
            </Button>
          </div>
        </ExpandableSection>
      </div>
    </div>
  );
};

export default ContactMethod;
